/**
 * Los permisos de Socio Amigo que la interfaz mira.
 *
 * La lista buena es la del API (`transport/catalog/permissions.ts`): de ahí
 * salen los que se dan de alta en lala-ms, y el API valida cada herramienta
 * contra ellos. Aquí solo están los que deciden qué se ENSEÑA, con el mismo
 * texto, para que un nombre mal escrito no compile en vez de esconder un botón
 * a todo el mundo sin que nadie se entere.
 *
 * La interfaz esconde; el backend decide. Quitar un botón no protege nada.
 */
export const PERMISSIONS = {
  /** Entrar a la aplicación (§10.2). */
  access: "socio-amigo:global.view",
  /** Todo: satisface cualquier otro, incluido `access` (§10.2). */
  manage: "socio-amigo:global.manage",

  memoryWrite: "socio-amigo:memory.write",
  /** El consumo de tokens: la sección de Work y el contador de la cabecera. */
  usageRead: "socio-amigo:usage.read",
  /** Sincronizar o fijar los precios de los modelos. */
  usageWrite: "socio-amigo:usage.write",
  /** Ver el proceso de revisión de documentos y el seguimiento de cada uno. */
  invoiceReviewsRead: "socio-amigo:document_reviews.read",
  /** Digitar: tomar un documento, editar sus líneas y decidir. */
  invoiceReviewsDecide: "socio-amigo:document_reviews.decide",
  /** Meter documentos al proceso de revisión y reprocesar los que fallaron. */
  invoiceReviewsRun: "socio-amigo:document_reviews.run",
  /** «Acierto»: lo que acierta el proceso de revisión y sus alertas. */
  accuracyRead: "socio-amigo:accuracy.read",
  /** Marcar alertas del acierto como atendidas. */
  accuracyManage: "socio-amigo:accuracy.manage",
  /** La auditoría (`get_activity`). Aún no la usa ninguna pantalla. */
  activityRead: "socio-amigo:activity.read",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Si quien tiene `held` puede `permission`. LA ÚNICA regla del front.
 *
 * `socio-amigo:global.manage` satisface cualquier permiso, igual que en el
 * chequeo del API: es una regla, no una lista, para que un permiso nuevo no se
 * quede sin él por olvido. `/me` devuelve los permisos tal cual, sin
 * expandir, así que la expansión vive aquí.
 */
export const hasPermission = (
  held: readonly string[],
  permission: Permission,
): boolean => held.includes(PERMISSIONS.manage) || held.includes(permission);

/** El rol de auth que ve los costos. No es un permiso: ver {@link canSeeCosts}. */
export const SUPER_ADMIN_ROLE = "SUPER_ADMIN";

/**
 * Si quien es `me` ve los costos: el costo y el consumo de IA, en dólares y
 * en tokens. LA EXCEPCIÓN a `hasPermission`, y la única.
 *
 * Va por el ROL y no por un permiso porque ningún permiso lo dice:
 * `global.manage` lo tienen también PROGRAM_ADMIN y BUSINESS_OWNER, y vale
 * por `usage.read`. Solo esconde: el API no cambia, y quien tenga
 * `usage.read` lo puede seguir pidiendo. El P.V.P. y la señal de precio de
 * las líneas son del negocio, no costos, y no pasan por aquí.
 */
export const canSeeCosts = (me: { readonly roles: readonly string[] }) =>
  me.roles.includes(SUPER_ADMIN_ROLE);

/**
 * Si quien es `me` es el admin de Alfred: el que ve todos los pedidos,
 * cambia su estado y responde. Es el mismo rol que ve los costos, y como
 * aquel, solo decide lo que se DICE --«Todos los pedidos» o «Tus
 * pedidos»--: qué pedidos llegan y qué botones salen lo decide el API
 * (`list_alfred_tickets`, `can*` y `allowedTransitions` del detalle).
 */
export const isAlfredAdmin = (me: { readonly roles: readonly string[] }) =>
  me.roles.includes(SUPER_ADMIN_ROLE);
